import React, { useState, useEffect } from 'react';
import Loader from '../../components/Common/Loader';
import Collapse from '../../components/Common/Collapse';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getSections } from '../../services/studentService';
import SegToggle from '../../components/Common/SegToggle';
import LockedModal from '../../components/Student/LockedModal';

const TABS = [
  { key: 'all',    label: 'Все вебинары' },
  { key: 'active', label: 'Активные' },
  { key: 'viewed', label: 'Просмотренные' },
];

// Просмотренным считается вебинар с прогрессом «viewed» или «completed»
const isViewedLesson = (l) => l.progress?.status === 'viewed' || l.progress?.status === 'completed';

// Минуты → «X ч Y мин»
const fmtDur = (mins) => {
  mins = Math.round(mins || 0);
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h} ч${m ? ' ' + m + ' мин' : ''}` : `${m} мин`;
};

const MonthRu = ['ЯНВ','ФЕВ','МАР','АПР','МАЙ','ИЮН','ИЮЛ','АВГ','СЕН','ОКТ','НОЯ','ДЕК'];
const DaysRu = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];

// Русское склонение: plural(n, ['модуль','модуля','модулей'])
const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

const IcSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
// Иконка модуля = иконка раздела «Вебинары» из сайдбара (видео)
const IcSection = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
  </svg>
);
const IcChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IcLock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
);
const IcArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

export default function LessonsPage() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [open, setOpen] = useState({}); // { [sectionId]: bool }
  const [lockInfo, setLockInfo] = useState(null);   // занятие вне пробного доступа — показываем модалку
  const [, setTick] = useState(0); // перерасчёт «в эфире» по таймеру (без перезагрузки)
  const navigate = useNavigate();
  const { selectedCourseId } = useOutletContext() || {};

  // Пересчитываем состояние каждые 30 сек, чтобы «Войти/В эфире» появлялись и
  // пропадали вовремя, пока ученик сидит на странице.
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setLoading(true);
    getSections(selectedCourseId)
      .then(res => {
        const data = res.data || [];
        setSections(data);
        // Все модули закрыты по умолчанию (как в разделе «Задания»). «Текущий» модуль
        // только подсвечивается (обводка + бейдж), но сам не раскрывается.
        setOpen({});
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedCourseId]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const q = search.trim().toLowerCase();
  const open2 = (id) => navigate(`/lesson/${id}`, { state: { from: '/lessons' } });
  const toggle = (id) => setOpen(o => ({ ...o, [id]: !o[id] }));

  // Идёт ли урок прямо сейчас (МСК): от начала и до конца по длительности урока
  // (как на странице урока и на бэкенде). Длительность не задана → 120 минут.
  const liveCheck = (l) => {
    if (!l.date || !l.time) return false;
    const start = new Date(`${l.date}T${l.time.slice(0, 5)}:00+03:00`);
    if (isNaN(start)) return false;
    const now = new Date();
    const durMs = (l.duration > 0 ? l.duration : 120) * 60 * 1000;
    return now >= start && (now - start) < durMs;
  };

  // Модули с вебинарами (после поиска). ДЗ-без-вебинара сюда не попадают.
  const searchedModules = sections.map(s => ({
    ...s,
    lessons: (s.Lessons || [])
      .filter(l => !l.isHomeworkOnly)
      .slice()
      // Порядок задаёт админ перетаскиванием (sortOrder), а не дата.
      .sort((a, b) => (a.sortOrder ?? a.sort_order ?? 0) - (b.sortOrder ?? b.sort_order ?? 0))
      .filter(l => !q || l.title.toLowerCase().includes(q)),
  }));

  // Счётчики для тумблера — по отдельным вебинарам
  const allLessons = searchedModules.flatMap(m => m.lessons);
  const counts = {
    all: allLessons.length,
    active: allLessons.filter(l => !isViewedLesson(l)).length,
    viewed: allLessons.filter(isViewedLesson).length,
  };

  // Модули, в которых ПРЯМО СЕЙЧАС идёт вебинар — для мигающей точки на иконке модуля
  // (независимо от вкладки; пересчитывается каждые 30 сек вместе с liveCheck).
  const liveModuleIds = new Set(searchedModules.filter(m => m.lessons.some(liveCheck)).map(m => m.id));

  // Применяем фильтр вкладки
  const modules = searchedModules
    .map(m => ({ ...m, lessons: m.lessons.filter(l => tab === 'all' ? true : tab === 'viewed' ? isViewedLesson(l) : !isViewedLesson(l)) }))
    .filter(m => m.lessons.length > 0);

  if (loading) return <div className="s-page"><Loader /></div>;

  return (
    <div className="s-page">
      <div className="s-page-header">
        <div>
          <h1 className="s-page-title">Вебинары</h1>
          <p className="s-page-sub">Программа курса · {modules.length} {plural(modules.length, ['модуль', 'модуля', 'модулей'])}</p>
        </div>
      </div>

      {/* Поиск слева + тумблер справа — одной строкой */}
      <div className="s-filter-row">
        <div className="pg-search">
          <IcSearch />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск вебинара..." />
        </div>
        <SegToggle
          className="glass"
          value={tab}
          onChange={setTab}
          options={TABS.map(t => ({ key: t.key, label: t.label, count: counts[t.key] > 0 ? counts[t.key] : null }))}
        />
      </div>

      {modules.length === 0 ? (
        <div className="pg-empty">{q ? 'Ничего не найдено' : (tab === 'all' ? 'Модули ещё не добавлены' : 'Нет вебинаров в этой категории')}</div>
      ) : (
        modules.map(mod => {
          const isOpen = (q || tab !== 'all') ? true : !!open[mod.id]; // при поиске/фильтре раскрываем все
          const lessons = mod.lessons;
          const n = lessons.length;
          const totalMin = lessons.reduce((s, l) => s + (l.duration || 0), 0);
          const viewed = lessons.filter(isViewedLesson).length;
          const pct = n ? Math.round((viewed / n) * 100) : 0;
          const modHasLive = liveModuleIds.has(mod.id); // в модуле идёт вебинар прямо сейчас
          const modCard = (
            <div className={`mod${isOpen ? ' open' : ''}${mod.isCurrent ? ' current' : ''}`}>
              <div className="mod-head" onClick={() => toggle(mod.id)}>
                <div className="mod-ic">{(mod.isCurrent || modHasLive) && <span className={`mod-cur-dot${modHasLive ? ' live' : ''}`} />}<IcSection /></div>
                <div className="mod-info">
                  <div className="mod-title">{mod.name}</div>
                  <div className="mod-sub">{n} {plural(n, ['вебинар', 'вебинара', 'вебинаров'])}{totalMin > 0 ? ` · ${fmtDur(totalMin)}` : ''}</div>
                </div>
                <div className="mod-prog">
                  <div className="mod-prog-top"><span>Просмотрено {viewed} / {n}</span><b>{pct}%</b></div>
                  <div className="mod-prog-bar"><span style={{ width: `${pct}%` }} /></div>
                </div>
                <span className="mod-count">{n}</span>
                <span className="mod-chev"><IcChevron /></span>
              </div>

              <Collapse open={isOpen} keepMounted>
                <div className="mod-body">
                  {lessons.length === 0 ? (
                    <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '8px 0' }}>В этом модуле нет уроков</div>
                  ) : (
                    <div className="tline">
                      {lessons.map(lesson => {
                        const hasDate = !!lesson.date;
                        const d = hasDate ? new Date(lesson.date) : null;
                        const isToday = d && d.toDateString() === new Date().toDateString();
                        const isPast = d && d < today;
                        const isDone = lesson.progress?.status === 'completed';
                        const isViewed = !isDone && lesson.progress?.status === 'viewed';
                        const isLive = liveCheck(lesson);
                        // Эфир урока уже закончился (по длительности), даже если он сегодня
                        const endedWebinar = isPast || (() => {
                          if (!lesson.date || !lesson.time) return false;
                          const start = new Date(`${lesson.date}T${lesson.time.slice(0, 5)}:00+03:00`);
                          if (isNaN(start)) return false;
                          const durMs = (lesson.duration > 0 ? lesson.duration : 120) * 60 * 1000;
                          return new Date() > new Date(start.getTime() + durMs);
                        })();

                        // Точка слева: зелёная — пройден; жёлтая — просмотрен;
                        // фиолетовая — непройденный сегодняшний/идущий.
                        const itemCls = isDone ? ' done' : (isViewed ? ' viewed' : ((isToday || isLive) ? ' today' : ''));
                        // Плашка даты серая и у прошедших, и у будущих — выделяем только сегодняшний/идущий
                        const badgeCls = (isToday || isLive) ? ' today' : ' past';

                        const locked = !!lesson.locked;   // пробный доступ: занятие не открыто

                        return (
                          <div key={lesson.id} className={`titem${itemCls}${locked ? ' locked' : ''}`}>
                            <div className={`ccard${isToday && !locked ? ' today' : ''}${locked ? ' locked' : ''}`}
                              onClick={() => (locked ? setLockInfo({ kind: 'webinar', title: lesson.title }) : open2(lesson.id))}>
                              <div className={`dbadge${badgeCls}`}>
                                {hasDate ? (
                                  <><span className="d">{d.getDate()}</span><span className="m">{MonthRu[d.getMonth()]}</span></>
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-dark)' }}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                                )}
                              </div>
                              <div className="linfo">
                                <div className="lname">{lesson.title}</div>
                                <div className="lmeta">
                                  {hasDate && <span><b>{DaysRu[d.getDay()]}</b>{lesson.time ? `, ${lesson.time.slice(0, 5)}` : ''}</span>}
                                  {lesson.duration > 0 && <span>· {fmtDur(lesson.duration)}</span>}
                                  {lesson.hwCount > 0 && <span>· ДЗ {lesson.hwCount} {plural(lesson.hwCount, ['задача', 'задачи', 'задач'])}</span>}
                                </div>
                              </div>
                              <div className="lright">
                                {locked ? (
                                  <span className="lock-chip" title="Откроется с полным курсом"><IcLock /></span>
                                ) : (
                                  <>
                                    {isLive && <span className="pg-chip live"><span className="pg-live-dot" />В эфире</span>}
                                    {isDone && <span className="pg-chip green">Пройден</span>}
                                    {!isDone && lesson.progress?.status === 'viewed' && <span className="pg-chip amber">Просмотрен</span>}
                                    {isToday && !isLive && <span className="pg-chip violet">Сегодня</span>}
                                    <button
                                      className={`pg-btn${isLive ? ' green' : (endedWebinar ? ' ghost' : '')}`}
                                      onClick={e => { e.stopPropagation(); open2(lesson.id); }}
                                    >
                                      {isLive ? 'Войти' : (endedWebinar ? 'Подробнее' : 'Перейти')}{!endedWebinar && !isLive && <IcArrow />}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Collapse>
            </div>
          );
          // Текущий модуль виден по рамке и точке на иконке — отдельной плашки нет
          return <React.Fragment key={mod.id}>{modCard}</React.Fragment>;
        })
      )}

      <LockedModal open={!!lockInfo} onClose={() => setLockInfo(null)}
        kind={lockInfo?.kind} title={lockInfo?.title} />
    </div>
  );
}
